import React from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, BuildingIcon, CardIcon, CheckIcon, ArrowRightIcon } from '../components/common/Icons';

const Home: React.FC = () => {
  return (
    <div className="bg-white">
      {/* 히어로 섹션 - BigValue 스타일 */}
      <section className="relative bg-gradient-to-br from-primary-50 via-white to-secondary-50 overflow-hidden">
        {/* 배경 장식 요소 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-200 rounded-full opacity-30 blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-secondary-200 rounded-full opacity-30 blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-primary-500 text-white px-5 py-2.5 rounded-full text-sm font-semibold mb-8 shadow-lg">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
              AI 기반 고객 맞춤형 데이터 플랫폼
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-tight">
              당신의 분석 여정을<br />
              <span className="text-primary-500">단순하게</span>, 결과는<br />
              <span className="text-primary-500">풍부하게</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed">
              빅데이터·인공지능(AI) 기반의 플랫폼 서비스로<br />
              금융·유통·건설 등 비즈니스 문제를 원스톱 해결합니다.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 bg-primary-500 text-white px-10 py-4 rounded-xl text-lg font-bold hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                무료로 시작하기
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
              <Link
                to="/developer-tools/sandbox"
                className="inline-flex items-center justify-center gap-2 bg-white text-primary-600 px-10 py-4 rounded-xl text-lg font-bold border-2 border-primary-500 hover:bg-primary-50 transition-all shadow-md hover:shadow-lg"
              >
                데모 체험하기
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 통계 섹션 - BigValue 스타일 */}
      <section className="py-20 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200">
              <div className="text-5xl md:text-6xl font-bold text-primary-600 mb-2">4억+</div>
              <div className="text-primary-700 text-lg font-semibold">데이터 건수</div>
            </div>
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-secondary-50 to-secondary-100 border border-secondary-200">
              <div className="text-5xl md:text-6xl font-bold text-secondary-600 mb-2">0.95</div>
              <div className="text-secondary-700 text-lg font-semibold">AI 정확도 (R²)</div>
            </div>
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-primary-50 to-primary-100 border border-primary-200">
              <div className="text-5xl md:text-6xl font-bold text-primary-600 mb-2">99.5%</div>
              <div className="text-primary-700 text-lg font-semibold">전국 커버리지</div>
            </div>
            <div className="text-center p-6 rounded-2xl bg-gradient-to-br from-secondary-50 to-secondary-100 border border-secondary-200">
              <div className="text-5xl md:text-6xl font-bold text-secondary-600 mb-2">99.9%</div>
              <div className="text-secondary-700 text-lg font-semibold">서비스 가용성</div>
            </div>
          </div>
        </div>
      </section>

      {/* 주요 데이터 API */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              다양한 데이터를 <span className="text-primary-500">하나의 플랫폼</span>으로
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              부동산, 사업자, 카드 데이터부터 AI 분석까지<br/>
              모든 데이터 솔루션을 통합된 인터페이스로 제공합니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* 부동산 데이터 */}
            <div className="group relative bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-primary-500 transition-all duration-300 hover:shadow-xl overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full -mr-16 -mt-16 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <HomeIcon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">부동산 데이터</h3>
                <p className="text-gray-600 mb-8 text-base leading-relaxed">
                  실거래가, 시세 정보, 권리분석, AI 기반 가격 예측까지 제공하는 통합 부동산 데이터 솔루션
                </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  실시간 시세 정보
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  AI 기반 가격 예측
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  권리분석 리포트
                </li>
              </ul>
                <Link
                  to="/api-docs/realestate"
                  className="inline-flex items-center text-primary-600 font-bold hover:gap-2 transition-all group-hover:text-primary-700"
                >
                  자세히 보기
                  <ArrowRightIcon className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* 사업자 데이터 */}
            <div className="group relative bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-primary-500 transition-all duration-300 hover:shadow-xl overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full -mr-16 -mt-16 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BuildingIcon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">사업자 데이터</h3>
                <p className="text-gray-600 mb-8 text-base leading-relaxed">
                  사업자등록정보, 휴폐업 조회, 기업 신용정보를 실시간으로 확인하는 비즈니스 솔루션
                </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  사업자 진위 확인
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  휴폐업 실시간 조회
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  기업 신용정보
                </li>
              </ul>
                <Link
                  to="/api-docs/business"
                  className="inline-flex items-center text-primary-600 font-bold hover:gap-2 transition-all group-hover:text-primary-700"
                >
                  자세히 보기
                  <ArrowRightIcon className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            {/* 카드 데이터 */}
            <div className="group relative bg-white rounded-2xl p-8 border-2 border-gray-200 hover:border-primary-500 transition-all duration-300 hover:shadow-xl overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full -mr-16 -mt-16 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <CardIcon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">카드 데이터</h3>
                <p className="text-gray-600 mb-8 text-base leading-relaxed">
                  카드 승인내역, 지출 패턴 분석, 가맹점 정보를 AI로 분석하는 핀테크 솔루션
                </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  실시간 승인내역
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  지출 패턴 분석
                </li>
                <li className="flex items-center text-gray-700">
                  <CheckIcon className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                  가맹점 정보 조회
                </li>
              </ul>
                <Link
                  to="/api-docs/card"
                  className="inline-flex items-center text-primary-600 font-bold hover:gap-2 transition-all group-hover:text-primary-700"
                >
                  자세히 보기
                  <ArrowRightIcon className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 고객 사례 */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              빅밸류와 함께한 <span className="text-primary-500">성공 사례</span>
            </h2>
            <p className="text-xl text-gray-600">
              다양한 산업군의 고객들이 빅밸류로 성과를 만들어가고 있습니다
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* 케이스 스터디 1 */}
            <div className="bg-gradient-to-br from-primary-50 to-white border-2 border-primary-200 rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center text-2xl font-bold text-white mr-4 shadow-lg">
                  A
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">A 은행</h3>
                  <p className="text-gray-600">부동산 담보대출 심사</p>
                </div>
              </div>
              <p className="text-gray-700 mb-8 text-lg leading-relaxed">
                빅밸류의 부동산 시세 정보와 권리분석 AI를 활용하여 대출 심사 프로세스를 혁신했습니다.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-8 p-6 bg-white rounded-xl shadow-inner border border-primary-100">
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">67%</div>
                  <div className="text-sm text-gray-600">심사 시간 단축</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">10%p</div>
                  <div className="text-sm text-gray-600">정확도 향상</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">5억원</div>
                  <div className="text-sm text-gray-600">연간 비용 절감</div>
                </div>
              </div>
              <blockquote className="border-l-4 border-primary-500 pl-6 text-gray-700 italic text-lg font-medium">
                "3일이 걸리던 심사를 1일로 단축했습니다."
              </blockquote>
            </div>

            {/* 케이스 스터디 2 */}
            <div className="bg-gradient-to-br from-secondary-50 to-white border-2 border-secondary-200 rounded-2xl p-10 hover:shadow-xl transition-all">
              <div className="flex items-center mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-xl flex items-center justify-center text-2xl font-bold text-white mr-4 shadow-lg">
                  B
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">B 부동산 플랫폼</h3>
                  <p className="text-gray-600">시세 정보 서비스</p>
                </div>
              </div>
              <p className="text-gray-700 mb-8 text-lg leading-relaxed">
                실거래가 API와 시세 예측 API를 통합하여 사용자들에게 신뢰할 수 있는 부동산 정보를 제공합니다.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-8 p-6 bg-white rounded-xl shadow-inner border border-secondary-100">
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">25%p</div>
                  <div className="text-sm text-gray-600">만족도 향상</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">30%</div>
                  <div className="text-sm text-gray-600">사용자 증가</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-gray-900 mb-1">40%</div>
                  <div className="text-sm text-gray-600">수익 증가</div>
                </div>
              </div>
              <blockquote className="border-l-4 border-secondary-500 pl-6 text-gray-700 italic text-lg font-medium">
                "정확한 시세 정보 덕분에 사용자 신뢰도가 크게 상승했습니다."
              </blockquote>
            </div>
          </div>
        </div>
      </section>

      {/* CTA 섹션 */}
      <section className="relative py-24 bg-gradient-to-br from-primary-600 via-primary-500 to-secondary-500 overflow-hidden">
        {/* 배경 장식 */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            지금 바로 시작하세요
          </h2>
          <p className="text-xl text-primary-100 mb-12 max-w-2xl mx-auto">
            5분 안에 첫 API 호출을 완성하고<br/>
            빅밸류의 강력한 데이터 분석 기능을 경험해보세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center bg-white text-primary-600 px-10 py-4 rounded-xl text-lg font-bold hover:bg-gray-50 transition-all shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              무료로 시작하기
              <ArrowRightIcon className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to="/support/getting-started"
              className="inline-flex items-center justify-center bg-transparent border-2 border-white text-white px-10 py-4 rounded-xl text-lg font-bold hover:bg-white hover:text-primary-600 transition-all"
            >
              시작 가이드 보기
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
